import { Box, Grid, Typography, Link, Stack, Divider } from '@mui/material';
import React from 'react';

import { EnlargedImage } from '@app/components/Shared/EnlargedImage';
import { theme } from '@app/theme';
import {
  CategoryType,
  ProductVariant,
  ProductVariantFloorPlanCategory,
} from '@app/types/catalog';
import { formatPrice } from '@app/utils/format';

interface PropertyFloorPlanProps {
  organizationId: string;
  parentOrganizationId?: string;
  productId?: string;
  variants: ProductVariant[];
}

export function PropertyFloorPlan({
  productId,
  variants,
  parentOrganizationId,
}: PropertyFloorPlanProps) {
  return (
    <Stack width="100%" maxWidth={1040} mx="auto" gap={2} py={{ sm: 9, xs: 4 }}>
      <Typography
        width="100%"
        bgcolor={theme.palette.blueAccent.main}
        color="white"
        px={2}
        height={36}
        fontWeight={600}
        borderRadius={15}
        display="flex"
        alignItems="center"
        variant="h6"
      >
        間取り・プラン
      </Typography>
      <Typography>公開中：{variants.length}件</Typography>

      <Grid container columnSpacing={4} rowSpacing={4}>
        {variants.map((variant, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Stack
              width="100%"
              borderRadius={2}
              border={1}
              p={{ sm: 3, xs: 2 }}
              borderColor="#E0E0E0"
            >
              <Stack flexDirection={{ sm: 'column', xs: 'row' }}>
                <Typography fontSize="1.5rem" fontWeight={600} pb={1}>
                  {variant.title}
                </Typography>
                <Divider
                  sx={{
                    border: 'solid 1px #B4B4B4',
                    display: { md: 'none', sm: 'block', xs: 'none' },
                    mb: 2,
                    mt: 1,
                  }}
                />
                <Grid
                  container
                  width="100%"
                  mt={{ sm: 0, xs: 1 }}
                  ml={{ sm: 0, xs: 2 }}
                >
                  <Grid
                    item
                    xs={12}
                    md={6}
                    borderRight={{
                      md: `1px solid ${theme.palette.divider}`,
                      xs: 'none',
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent={{
                          md: 'flex-start',
                          sm: 'space-between',
                          xs: 'flex-start',
                        }}
                        spacing={1}
                      >
                        <Typography
                          color={theme.palette.blueAccent.main}
                          width={70}
                        >
                          間取り
                        </Typography>
                        <Typography>{variant.description || '-'}</Typography>
                      </Stack>
                      <Divider
                        sx={{
                          borderStyle: 'dashed',
                          display: { md: 'none', sm: 'block', xs: 'none' },
                        }}
                      />
                      <Stack
                        direction="row"
                        justifyContent={{
                          md: 'flex-start',
                          sm: 'space-between',
                          xs: 'flex-start',
                        }}
                        spacing={1}
                      >
                        <Typography
                          color={theme.palette.blueAccent.main}
                          width={70}
                        >
                          土地面積
                        </Typography>
                        <Typography>
                          {(Number(variant.customFields?.landArea) > 0 &&
                            variant.customFields?.landArea) ||
                            '-'}
                          ㎡
                        </Typography>
                      </Stack>
                      <Divider
                        sx={{
                          borderStyle: 'dashed',
                          display: { md: 'none', sm: 'block', xs: 'none' },
                        }}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6} pl={{ md: 2, xs: 0 }}>
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent={{
                          md: 'flex-start',
                          sm: 'space-between',
                          xs: 'flex-start',
                        }}
                        spacing={1}
                      >
                        <Typography
                          color={theme.palette.blueAccent.main}
                          width={70}
                        >
                          建物面積
                        </Typography>
                        <Typography>
                          {(Number(variant.customFields?.buildingArea) > 0 &&
                            variant.customFields?.buildingArea) ||
                            '-'}{' '}
                          ㎡
                        </Typography>
                      </Stack>
                      <Divider
                        sx={{
                          borderStyle: 'dashed',
                          display: { md: 'none', sm: 'block', xs: 'none' },
                        }}
                      />
                      <Stack
                        direction="row"
                        justifyContent={{
                          md: 'flex-start',
                          sm: 'space-between',
                          xs: 'flex-start',
                        }}
                        spacing={1}
                      >
                        <Typography
                          color={theme.palette.blueAccent.main}
                          width={70}
                        >
                          販売価格
                        </Typography>
                        <Typography variant="body2">
                          {variant.customFields?.price &&
                          Number(variant.customFields.price) > 0 &&
                          variant.customFields?.price.trim() !== '' ? (
                            <>
                              {formatPrice(Number(variant.customFields.price))}{' '}
                              万円
                            </>
                          ) : (
                            <>
                              <Link
                                href={`/companies/${parentOrganizationId}/inquiry/create?category=${CategoryType.PROPERTY}&productId=${productId}`}
                                color="primary"
                              >
                                価格をお問い合わせ（無料）
                              </Link>
                            </>
                          )}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
              {variant.images?.[0]?.url && (
                <Box width="100%" mt={{ sm: 2, xs: 1 }}>
                  <EnlargedImage
                    src={variant.images[0].url}
                    title={
                      variant.title ??
                      Object.values(ProductVariantFloorPlanCategory).find(
                        (category) =>
                          category.key === variant.customFields?.category
                      )?.label
                    }
                    objectFit="contain"
                  />
                </Box>
              )}
            </Stack>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}
